import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import * as React from "react";
import type { ToggleSize, ToggleVariant } from "../atoms/toggle.js";
import { cx } from "../internal/cx.js";

type ToggleGroupContextValue = {
  size: ToggleSize;
  variant: ToggleVariant;
};

const ToggleGroupContext = React.createContext<ToggleGroupContextValue>({
  size: "md",
  variant: "outline",
});

export type ToggleGroupProps = React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> & {
  size?: ToggleSize;
  variant?: ToggleVariant;
};

/**
 * Composes pressed-button states into single-select or multi-select
 * control groups. Use for view modes, formatting controls, or compact
 * filter choices. For mutually-exclusive actions without selected state,
 * prefer `<ButtonGroup>`; for a single on/off setting prefer `<Switch>`.
 */
export const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  ToggleGroupProps
>(function ToggleGroup({ children, className, size = "md", variant = "outline", ...props }, ref) {
  return (
    <ToggleGroupPrimitive.Root ref={ref} className={cx("cw-v2-toggle-group", className)} {...props}>
      <ToggleGroupContext.Provider value={{ size, variant }}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
});
ToggleGroup.displayName = "ToggleGroup";

export interface ToggleGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> {
  size?: ToggleSize;
  variant?: ToggleVariant;
}

export const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  ToggleGroupItemProps
>(function ToggleGroupItem({ className, size, variant, ...props }, ref) {
  const context = React.useContext(ToggleGroupContext);
  const resolvedSize = size ?? context.size;
  const resolvedVariant = variant ?? context.variant;

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cx(
        "cw-v2-toggle",
        `cw-v2-toggle--${resolvedVariant}`,
        `cw-v2-toggle--${resolvedSize}`,
        "cw-v2-toggle-group__item",
        className,
      )}
      {...props}
    />
  );
});
ToggleGroupItem.displayName = "ToggleGroupItem";
